import { useEffect, useRef } from "react";
import { BRAIN_TOPICS_REMOTE_STATUS } from "../../config/mqtt_config";
import type { VehicleSessionSnapshot } from "../../interface/auth_types";
import {
  encodeRemoteSystemStatus,
  type RemoteSystemConnectionStatus,
} from "../../interface/mqtt_codec";
import type SocketMqttBridge from "../../interface/socket_mqtt_bridge";
import type { CameraFpsMap } from "../../types/camera_fps";

const REMOTE_SYSTEM_STATUS_PUBLISH_INTERVAL_MS = 1000;

function getRemoteSystemConnectionStatus(
  snapshot: VehicleSessionSnapshot,
): RemoteSystemConnectionStatus {
  const mqttConnected = snapshot.mqttTransportState === "connected";
  const webrtcConnected = snapshot.remoteVideoReady === true;

  if (mqttConnected && webrtcConnected) {
    return "both";
  }

  if (mqttConnected) {
    return "mqtt";
  }

  if (webrtcConnected) {
    return "webrtc";
  }

  return "none";
}

export default function useRemoteSystemStatusPublisher(
  bridge: SocketMqttBridge | null,
  cameraReceiverFps: CameraFpsMap,
) {
  const latestCameraReceiverFpsRef = useRef<CameraFpsMap>({});
  const latestVehicleSessionSnapshotRef =
    useRef<VehicleSessionSnapshot | null>(null);

  useEffect(() => {
    latestCameraReceiverFpsRef.current = cameraReceiverFps;
  }, [cameraReceiverFps]);

  useEffect(() => {
    if (!bridge) {
      return undefined;
    }

    latestVehicleSessionSnapshotRef.current = bridge.getVehicleSessionSnapshot();
    const unsubscribeVehicleSession = bridge.subscribeVehicleSession(
      (snapshot) => {
        latestVehicleSessionSnapshotRef.current = snapshot;
      },
    );

    const publishRemoteSystemStatus = () => {
      const snapshot =
        latestVehicleSessionSnapshotRef.current ??
        bridge.getVehicleSessionSnapshot();

      if (!snapshot.vehicleId || snapshot.mqttTransportState !== "connected") {
        return;
      }

      if (!bridge.isMqttConnected()) {
        return;
      }

      bridge.sendMqtt(
        BRAIN_TOPICS_REMOTE_STATUS.SYSTEM,
        encodeRemoteSystemStatus({
          cameraFps: latestCameraReceiverFpsRef.current,
          connection: getRemoteSystemConnectionStatus(snapshot),
        }),
      );
    };

    const intervalId = window.setInterval(
      publishRemoteSystemStatus,
      REMOTE_SYSTEM_STATUS_PUBLISH_INTERVAL_MS,
    );

    return () => {
      unsubscribeVehicleSession();
      window.clearInterval(intervalId);
    };
  }, [bridge]);
}
